/**
 * Handles communication with the extension via window.postMessage
 */

import type { HelperMessage } from './types';

/**
 * Send a message to the extension via window.postMessage
 */
export function postMessageToExtension(
	message: HelperMessage,
	debug = false,
): void {
	try {
		// Post message to window - the content script will pick it up
		window.postMessage(message, '*');

		if (debug) {
			console.log(
				'[GraphQL Stream Viewer Helper] Message sent:',
				message.type,
				message,
			);
		}
	} catch (error) {
		if (debug) {
			console.error(
				'[GraphQL Stream Viewer Helper] Failed to send message:',
				error,
			);
		}
	}
}

/**
 * Check if the extension is available by looking for a specific marker
 * Note: This is a best-effort check and may not be 100% accurate
 */
export function isExtensionAvailable(): boolean {
	// Check if we're in a browser environment
	if (typeof window === 'undefined') {
		return false;
	}

	// Try to detect if the extension's content script is loaded
	// The content script should set a marker on the window object
	return '__GRAPHQL_STREAM_VIEWER_EXTENSION__' in window;
}

/**
 * Wait for the extension to become available (with timeout)
 */
export function waitForExtension(timeoutMs = 5000): Promise<boolean> {
	return new Promise((resolve) => {
		if (isExtensionAvailable()) {
			resolve(true);
			return;
		}

		const startTime = Date.now();
		const checkInterval = setInterval(() => {
			if (isExtensionAvailable()) {
				clearInterval(checkInterval);
				resolve(true);
			} else if (Date.now() - startTime >= timeoutMs) {
				clearInterval(checkInterval);
				resolve(false);
			}
		}, 100);
	});
}
